import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import {
  InköpKort, Producent, hämtaInköp, hämtaProducenter, skapaInköp, valutabelopp, kr, datumText, felText,
} from '../api/inkop';
import { sida, rubrik, kort, fält, knapp, knappSpök, etikett, tabell, th, td, tal, märke, statusfärg } from '../components/inkopStil';

type Filter = 'pagaende' | 'avslutade' | 'alla';
const FILTER: [Filter, string][] = [['pagaende', 'Pågående'], ['avslutade', 'Avslutade'], ['alla', 'Alla']];
const PÅGÅENDE = ['dialog', 'bekraftat', 'pagar', 'inlevererat'];

export default function InkopLista() {
  const navigate = useNavigate();
  const ärMobil = useMobil();
  const [inköp, setInköp] = useState<InköpKort[] | null>(null);
  const [producenter, setProducenter] = useState<Producent[]>([]);
  const [filter, setFilter] = useState<Filter>('pagaende');
  const [fel, setFel] = useState<string | null>(null);
  const [nytt, setNytt] = useState(false);
  const [producentId, setProducentId] = useState('');
  const [nyRubrik, setNyRubrik] = useState('');
  const [skapar, setSkapar] = useState(false);

  useEffect(() => {
    hämtaInköp().then(r => setInköp(r.data)).catch(e => setFel(felText(e, 'Kunde inte hämta inköpen.')));
    hämtaProducenter().then(r => setProducenter(r.data)).catch(() => {});
  }, []);

  const synliga = useMemo(() => (inköp || []).filter(i =>
    filter === 'alla' ? true : filter === 'pagaende' ? PÅGÅENDE.includes(i.status) : !PÅGÅENDE.includes(i.status),
  ), [inköp, filter]);

  async function skapa(e: React.FormEvent) {
    e.preventDefault();
    if (!producentId) return;
    setSkapar(true);
    try {
      const res = await skapaInköp({ producent_id: producentId, rubrik: nyRubrik || undefined });
      navigate(`/inkop/lista/${res.data.id}`);
    } catch (err) {
      setFel(felText(err, 'Kunde inte skapa inköpet.'));
      setSkapar(false);
    }
  }

  return (
    <div style={sida}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h1 style={rubrik}>Pågående inköp</h1>
        <button style={knapp} onClick={() => setNytt(n => !n)}>+ Nytt inköp</button>
      </div>

      {nytt && (
        <form onSubmit={skapa} style={{ ...kort, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '14px', alignItems: 'end' }}>
          <div>
            <label style={etikett} htmlFor="ny-producent">Producent</label>
            <select id="ny-producent" style={fält} value={producentId} onChange={e => setProducentId(e.target.value)} required>
              <option value="">Välj producent</option>
              {producenter.filter(p => p.aktiv).map(p => <option key={p.id} value={p.id}>{p.namn}</option>)}
            </select>
          </div>
          <div>
            <label style={etikett} htmlFor="ny-rubrik">Rubrik (valfri)</label>
            <input id="ny-rubrik" style={fält} value={nyRubrik} onChange={e => setNyRubrik(e.target.value)} placeholder="Till exempel Vårsläppet 2027" />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={{ ...knapp, opacity: skapar ? 0.6 : 1 }} disabled={skapar}>Skapa</button>
            <button type="button" style={knappSpök} onClick={() => setNytt(false)}>Avbryt</button>
          </div>
          {producenter.length === 0 && (
            <p style={{ margin: 0, fontSize: '13px' }}>
              Det finns inga producenter än. <Link to="/inkop/producenter" style={{ color: TEMA.accent }}>Lägg upp en producent</Link> först.
            </p>
          )}
        </form>
      )}

      <div style={{ display: 'flex', gap: '20px', borderBottom: `1px solid ${TEMA.border}`, marginBottom: '16px' }}>
        {FILTER.map(([f, text]) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px', marginBottom: '-1px',
            fontFamily: "'Inter', sans-serif", fontSize: '14px',
            color: filter === f ? TEMA.ink : TEMA.inkFaint,
            borderBottom: `2px solid ${filter === f ? TEMA.accent : 'transparent'}`,
          }}>
            {text}{inköp ? ` (${(inköp || []).filter(i => f === 'alla' ? true : f === 'pagaende' ? PÅGÅENDE.includes(i.status) : !PÅGÅENDE.includes(i.status)).length})` : ''}
          </button>
        ))}
      </div>

      {fel && <p style={{ color: '#D9695F', fontSize: '13px' }}>{fel}</p>}
      {!inköp && !fel && <p>Laddar...</p>}
      {inköp && synliga.length === 0 && (
        <p style={{ fontSize: '14px' }}>{filter === 'pagaende' ? 'Inga pågående inköp. Skapa ett med knappen ovan.' : 'Inga inköp här.'}</p>
      )}

      {/* På mobil får sex kolumner inte plats; varje inköp blir ett kort med samma uppgifter. */}
      {synliga.length > 0 && ärMobil && (
        <div>
          {synliga.map(i => (
            <Link key={i.id} to={`/inkop/producenter/${i.producent_id}?inkop=${i.id}`} style={{
              display: 'block', textDecoration: 'none', padding: '12px 0', borderBottom: `1px solid ${TEMA.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '14.5px', color: TEMA.ink, minWidth: 0 }}>{i.producent_namn}</span>
                <span style={{ ...märke(statusfärg(i.status)), flexShrink: 0 }}>{i.statusnamn}</span>
              </div>
              {(i.rubrik || i.importerat) && (
                <div style={{ fontSize: '12.5px', color: TEMA.inkFaint, marginTop: '2px' }}>
                  {[i.rubrik, i.importerat ? 'importerat' : null].filter(Boolean).join(' · ')}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '6px', fontSize: '12.5px', color: TEMA.inkSoft }}>
                <span>{i.bestalld ? `Beställt ${datumText(i.bestalld)}` : 'Inte beställt'} · <span style={tal}>{i.flaskor.toLocaleString('sv-SE')} fl</span></span>
                <span style={{ ...tal, color: TEMA.ink }}>{valutabelopp(i.belopp_valuta, i.valuta, 0)}{i.valuta && i.valuta !== 'SEK' && i.belopp_kr ? ` · ${kr(i.belopp_kr)}` : ''}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {synliga.length > 0 && !ärMobil && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tabell}>
            <thead>
              <tr>
                <th style={th}>Producent</th>
                <th style={th}>Status</th>
                <th style={th}>Beställt</th>
                <th style={{ ...th, ...tal }}>Flaskor</th>
                <th style={{ ...th, ...tal }}>Belopp</th>
                <th style={{ ...th, ...tal }}>I kronor</th>
              </tr>
            </thead>
            <tbody>
              {synliga.map(i => (
                <tr key={i.id} onClick={() => navigate(`/inkop/producenter/${i.producent_id}?inkop=${i.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={td}>
                    <span style={{ color: TEMA.ink }}>{i.producent_namn}</span>
                    {i.rubrik && <span style={{ color: TEMA.inkFaint }}> · {i.rubrik}</span>}
                    {i.importerat && <span style={{ color: TEMA.inkFaint, fontSize: '11.5px' }}> · importerat</span>}
                  </td>
                  <td style={td}><span style={märke(statusfärg(i.status))}>{i.statusnamn}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{datumText(i.bestalld)}</td>
                  <td style={{ ...td, ...tal }}>{i.flaskor.toLocaleString('sv-SE')}</td>
                  <td style={{ ...td, ...tal }}>{valutabelopp(i.belopp_valuta, i.valuta, 0)}</td>
                  <td style={{ ...td, ...tal }}>{kr(i.belopp_kr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
